import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';
import './NotificationConfigPanel.css';

/**
 * Reusable reminder-configuration panel.
 *
 * Drop it into ANY module that has registered a reminder provider on the
 * backend — the whole screen is driven by what `/api/notification-config/:module`
 * returns (label, description, placeholder tokens), so a new module needs no
 * new component and no change here:
 *
 *     <NotificationConfigPanel module="clearance-form" />
 *
 * Access is enforced server-side (the module's own card + `approve`); the panel
 * simply renders the error the API returns rather than re-deriving the rule.
 */

const pad2 = (n) => String(n).padStart(2, '0');

// Qatar wall-clock, because the schedule is stored and evaluated in Qatar time
// (the server runs UTC) — showing the viewer's local zone would make the
// configured hour look wrong for anyone outside Qatar.
const qatarStamp = (value) => (value
    ? new Date(value).toLocaleString('en-GB', {
        timeZone: 'Asia/Qatar', day: '2-digit', month: 'short',
        year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false
    })
    : '—');

const NotificationConfigPanel = ({ module }) => {
    const [meta, setMeta] = useState(null);
    const [status, setStatus] = useState(null);
    const [form, setForm] = useState(null);
    const [senders, setSenders] = useState([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState('');       // 'test' | 'run' | 'dry'
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    // Result of the last "preview who is due" — the recipient list, not just a
    // count. A number alone cannot answer "is this about to mail the wrong
    // people", which is why anyone previews before enabling it.
    const [preview, setPreview] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get(`/api/notification-config/${module}`);
            if (res.data?.success) {
                const { config, status: st, ...rest } = res.data.data;
                setMeta(rest);
                setStatus(st);
                setForm({ ...config, ccEmails: (config.ccEmails || []).join(', ') });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load reminder settings');
        } finally {
            setLoading(false);
        }
    }, [module]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        let cancelled = false;
        axiosInstance.get(`/api/notification-config/${module}/senders`)
            .then(res => { if (!cancelled && res.data?.success) setSenders(res.data.data || []); })
            .catch(() => { /* the sender list is a convenience; the field stays typeable */ });
        return () => { cancelled = true; };
    }, [module]);

    const set = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setNotice('');
        // The saved settings (maxReminders especially) change who is due, so a
        // preview taken before the save no longer describes what would happen.
        setPreview(null);
        try {
            const res = await axiosInstance.put(`/api/notification-config/${module}`, {
                ...form,
                sendHour: Number(form.sendHour),
                sendMinute: Number(form.sendMinute),
                intervalHours: Number(form.intervalHours),
                maxReminders: Number(form.maxReminders),
                ccEmails: form.ccEmails
            });
            if (res.data?.success) {
                setStatus(res.data.data.status);
                setNotice(res.data.message || 'Saved');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save reminder settings');
        } finally {
            setSaving(false);
        }
    };

    const act = async (kind) => {
        setBusy(kind);
        setError('');
        setNotice('');
        setPreview(null);
        try {
            const url = kind === 'test'
                ? `/api/notification-config/${module}/test`
                : `/api/notification-config/${module}/run`;
            const body = kind === 'dry' ? { dryRun: true } : {};
            const res = await axiosInstance.post(url, body);
            setNotice(res.data?.message || 'Done');

            if (kind === 'dry') {
                // A dry run changes nothing server-side, so refetching would
                // only flash the loading card over the result the user asked
                // for. Always set a list — [] is what "nobody is due" looks
                // like, and it must render as that rather than as nothing.
                setPreview(res.data?.data?.recipients || []);
            } else if (kind === 'run') {
                load();   // lastRunAt / nextRunAt moved
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Request failed');
        } finally {
            setBusy('');
        }
    };

    if (loading) return <div className="ncfg-card ncfg-empty">Loading reminder settings…</div>;
    if (!form) return <div className="ncfg-card ncfg-empty">{error || 'Reminder settings unavailable'}</div>;

    return (
        <div className="ncfg">
            <form className="ncfg-card" onSubmit={save}>
                <div className="ncfg-head">
                    <div>
                        <h2>Email Reminders</h2>
                        <p>{meta?.description || `Automatic reminders for ${meta?.label || module}.`}</p>
                    </div>
                    <label className="ncfg-switch">
                        <input type="checkbox" checked={!!form.enabled} onChange={set('enabled')} />
                        <span>{form.enabled ? 'Enabled' : 'Disabled'}</span>
                    </label>
                </div>

                {error && <div className="ncfg-alert is-error">{error}</div>}
                {notice && <div className="ncfg-alert is-ok">{notice}</div>}

                {preview && (
                    <div className="ncfg-preview">
                        <div className="ncfg-preview-head">
                            <strong>Due right now</strong>
                            <button type="button" className="ncfg-preview-close" onClick={() => setPreview(null)}>
                                Dismiss
                            </button>
                        </div>
                        {preview.length === 0 ? (
                            <p className="ncfg-preview-empty">
                                Nobody is due — every assigned section is either signed off or has
                                already had its maximum number of reminders.
                            </p>
                        ) : (
                            <ul className="ncfg-preview-list">
                                {preview.map((r, i) => (
                                    <li key={`${r.to}-${i}`}>
                                        <span className="ncfg-preview-to">{r.to}</span>
                                        {r.preview?.length > 0 && (
                                            <ul className="ncfg-preview-items">
                                                {r.preview.map((line, j) => <li key={j}>{line}</li>)}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <p className="ncfg-preview-note">Nothing was sent — this is a preview.</p>
                    </div>
                )}

                <section className="ncfg-section">
                    <h3>Sender</h3>
                    <div className="ncfg-grid">
                        <label className="ncfg-field">
                            <span>Send mail as (username = email)</span>
                            <input
                                list={`ncfg-senders-${module}`}
                                type="email"
                                value={form.senderEmail || ''}
                                onChange={set('senderEmail')}
                                placeholder="hr@bradmagroup.com.qa"
                            />
                            <datalist id={`ncfg-senders-${module}`}>
                                {senders.map(s => <option key={s.email} value={s.email}>{s.name}</option>)}
                            </datalist>
                            <small>Sent through Microsoft Graph as this mailbox — no password is stored.</small>
                        </label>

                        <label className="ncfg-field">
                            <span>Display name</span>
                            <input type="text" value={form.senderName || ''} onChange={set('senderName')} />
                        </label>

                        <label className="ncfg-field">
                            <span>Transport</span>
                            <select value={form.transport} onChange={set('transport')}>
                                <option value="graph">Microsoft 365 (Graph)</option>
                                <option value="smtp">SMTP (fallback)</option>
                            </select>
                            <small>Graph uses the Microsoft app credentials already configured for this app.</small>
                        </label>

                        <label className="ncfg-field">
                            <span>Always CC (comma separated)</span>
                            <input type="text" value={form.ccEmails} onChange={set('ccEmails')} placeholder="hr@…, admin@…" />
                        </label>
                    </div>
                </section>

                <section className="ncfg-section">
                    <h3>Schedule</h3>
                    <div className="ncfg-grid">
                        <label className="ncfg-field ncfg-narrow">
                            <span>Send at (Qatar time)</span>
                            <div className="ncfg-time">
                                <select value={form.sendHour} onChange={set('sendHour')}>
                                    {Array.from({ length: 24 }, (_, h) => (
                                        <option key={h} value={h}>{pad2(h)}</option>
                                    ))}
                                </select>
                                <span>:</span>
                                <select value={form.sendMinute} onChange={set('sendMinute')}>
                                    {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => (
                                        <option key={m} value={m}>{pad2(m)}</option>
                                    ))}
                                </select>
                            </div>
                        </label>

                        <label className="ncfg-field ncfg-narrow">
                            <span>Repeat every (hours)</span>
                            <input type="number" min="1" max="720" value={form.intervalHours} onChange={set('intervalHours')} />
                            <small>24 = once a day at the time above.</small>
                        </label>

                        <label className="ncfg-field ncfg-narrow">
                            <span>Stop after (reminders)</span>
                            <input type="number" min="0" max="100" value={form.maxReminders} onChange={set('maxReminders')} />
                            <small>0 = keep reminding until the item is completed.</small>
                        </label>
                    </div>
                </section>

                <section className="ncfg-section">
                    <h3>Message</h3>
                    <div className="ncfg-grid ncfg-grid-1">
                        <label className="ncfg-field">
                            <span>Subject</span>
                            <input type="text" value={form.subject || ''} onChange={set('subject')} />
                        </label>
                        <label className="ncfg-field">
                            <span>Opening paragraph</span>
                            <textarea rows="3" value={form.intro || ''} onChange={set('intro')} />
                        </label>
                    </div>
                    {meta?.tokens?.length > 0 && (
                        <p className="ncfg-tokens">
                            Placeholders: {meta.tokens.map(t => <code key={t}>{t}</code>)}
                        </p>
                    )}
                </section>

                <div className="ncfg-actions">
                    <button type="submit" className="ncfg-btn is-primary" disabled={saving}>
                        {saving ? 'Saving…' : 'Save settings'}
                    </button>
                    <button type="button" className="ncfg-btn" disabled={!!busy} onClick={() => act('test')}>
                        {busy === 'test' ? 'Sending…' : 'Send test to me'}
                    </button>
                    <button type="button" className="ncfg-btn" disabled={!!busy} onClick={() => act('dry')}>
                        {busy === 'dry' ? 'Checking…' : 'Preview who is due'}
                    </button>
                    <button type="button" className="ncfg-btn" disabled={!!busy} onClick={() => act('run')}>
                        {busy === 'run' ? 'Sending…' : 'Send reminders now'}
                    </button>
                </div>
            </form>

            <div className="ncfg-card ncfg-status">
                <h3>Status</h3>
                <dl>
                    <dt>Next run</dt>
                    <dd>{form.enabled ? qatarStamp(status?.nextRunAt) : 'Reminders are disabled'}</dd>
                    <dt>Last run</dt>
                    <dd>{qatarStamp(status?.lastRunAt)}{status?.lastRunSummary ? ` — ${status.lastRunSummary}` : ''}</dd>
                    {status?.lastError && (
                        <>
                            <dt>Last error</dt>
                            <dd className="ncfg-err">{qatarStamp(status.lastErrorAt)} — {status.lastError}</dd>
                        </>
                    )}
                    <dt>Updated by</dt>
                    <dd>{status?.updatedByName || '—'}</dd>
                </dl>
                <p className="ncfg-note">All times are Qatar time (UTC+3).</p>
            </div>
        </div>
    );
};

export default NotificationConfigPanel;
